import { useState } from 'react'
import { Alert, AlertDescription, Badge, Button, Input } from '@/components/ui'
import { DETAIL_TITLE_CLASS } from '@/components/layout/PageHeader'
import { mailboxesService, type MailboxProvider, type ProviderClientConfig } from '@/services/mailboxes'
import { providerNeedsClient } from './mailboxListModel'
import { NO_CLIENT_YET, PROVIDER_SETTINGS, PROVIDER_SETTINGS_SENTENCE, errorMessage } from './mailboxesText'

interface ProviderSettingsProps {
  /** Only the providers with an `oauth_config_id`. */
  providers: readonly MailboxProvider[]
  configs: Record<string, ProviderClientConfig>
  onSaved: (configId: string, cfg: ProviderClientConfig) => void
}

/** What a person typed. A client ID nobody edited is absent, so it reads the stored one, even one that loads later. */
interface Draft {
  clientId?: string
  /** The client ID was typed before the stored config loaded. */
  early?: boolean
  clientSecret: string
}

/** What the client ID box shows and Save sends. SA-1401: a blank typed before the stored config loaded gives way to it. */
function clientIdOf(draft: Draft | undefined, cfg: ProviderClientConfig | undefined): string {
  const stored = cfg?.oauth_client_id ?? ''
  const typed = draft?.clientId
  if (typed === undefined) return stored
  return cfg !== undefined && draft?.early && typed.trim() === '' ? stored : typed
}

// Provider OAuth client registration — SA-947: the served providers that have one.
export function ProviderSettings({ providers, configs, onSaved }: ProviderSettingsProps) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // SA-1398: before the stored config loads (or when its read failed), a save would send a blank
  // client ID over the stored one, so it waits for one to be typed.
  const savable = (configId: string) =>
    configs[configId] !== undefined || (drafts[configId]?.clientId ?? '').trim() !== ''

  const handleSave = async (configId: string) => {
    const cfg = configs[configId]
    const draft = drafts[configId]
    if (!savable(configId)) return
    setSaving(configId)
    setError(null)
    try {
      const body: { oauth_client_id: string; oauth_client_secret?: string | null } = {
        oauth_client_id: clientIdOf(draft, cfg).trim(),
      }
      if (draft?.clientSecret) body.oauth_client_secret = draft.clientSecret
      const updated = await mailboxesService.putProviderConfig(configId, body)
      onSaved(configId, updated)
      setDrafts((prev) => ({
        ...prev,
        [configId]: { clientId: updated.oauth_client_id ?? '', clientSecret: '' },
      }))
    } catch (err) {
      setError(errorMessage(err, 'Failed to save provider configuration'))
    } finally {
      setSaving(null)
    }
  }

  return (
    <>
      <div>
        <h2 className={DETAIL_TITLE_CLASS}>{PROVIDER_SETTINGS}</h2>
        <p className="text-sm text-muted-foreground">{PROVIDER_SETTINGS_SENTENCE}</p>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-4">
        {providers.map((p) => {
          const configId = p.oauth_config_id as string
          const cfg = configs[configId]
          const draft = drafts[configId]
          const secretSet = cfg?.oauth_client_secret_set ?? false
          return (
            <div
              key={configId}
              role="group"
              aria-label={p.label}
              className="@container space-y-3 border-t border-border pt-4 first:border-t-0 first:pt-0"
            >
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">{p.label}</h3>
                {providerNeedsClient([p], configs) && <Badge variant="warning">{NO_CLIENT_YET}</Badge>}
              </div>
              <div className="flex flex-col gap-3 @md:flex-row @md:items-end">
                <div className="flex-1">
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    {p.label} client ID
                  </label>
                  <Input
                    value={clientIdOf(draft, cfg)}
                    onChange={(e) => {
                      const clientId = e.target.value
                      const early = cfg === undefined
                      setDrafts((prev) => ({ ...prev, [configId]: { clientSecret: prev[configId]?.clientSecret ?? '', clientId, early } }))
                    }}
                    placeholder="xxxxx.apps.googleusercontent.com"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                    {p.label} client secret
                    {secretSet && <Badge variant="success">set</Badge>}
                  </label>
                  <Input
                    type="password"
                    value={draft?.clientSecret ?? ''}
                    onChange={(e) => {
                      const clientSecret = e.target.value
                      setDrafts((prev) => ({ ...prev, [configId]: { ...prev[configId], clientSecret } }))
                    }}
                    placeholder={secretSet ? 'unchanged' : 'Enter client secret'}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSave(configId)}
                  disabled={saving === configId || !savable(configId)}
                >
                  {saving === configId ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
